import type { HttpErrorResponse } from '@angular/common/http';

import type { ID, PickActions } from '@core/ngrx-legacy';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  ngrxEvent,
  pipeState,
  scopeFactory,
} from '@core/ngrx-legacy';
import {
  WorkflowAutomation,
  WorkflowAutomationCreatePayload,
  WorkflowAutomationOptionsData,
  WorkflowAutomationUpdatePayload,
} from '@core/workflow-automations-types';

export const jobTemplateIdScope = (jobTemplateId: number) =>
  scopeFactory('jobTemplateIdScope', jobTemplateId);

const idSelector = ({ id }: { id: number }) => id;
const { actions, namespace, reducer, selectors } =
  createEntityCollectionStore<WorkflowAutomation>()('JobTemplateWorkflowAutomations', {
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);

      return {
        fetchJobTemplateAutomations: (state, payload: { jobTemplateId: number; scope: string }) =>
          adapter.markAsPending(state, { scope: null, entities: [payload.jobTemplateId] }),
        fetchJobTemplateAutomationsSuccess: (state, { data, scope }) =>
          pipeState(
            state,
            next => adapter.setMany(next, data, { scope }),
            next =>
              adapter.markAsLoaded(next, { scope, entities: data.map(({ id }: { id: ID }) => id) }),
          ),
        fetchJobTemplateAutomationsFailed: defaultActions.fetchOneFailed,
        createOnePessimistic: (
          state,
          payload: {
            jobTemplateId: number;
            data: WorkflowAutomationCreatePayload;
            scope: string;
            correlationId: string;
          },
        ) => adapter.markAsPending(state),
        updateOnePessimistic: (
          state,
          payload: {
            jobTemplateId: number;
            data: WorkflowAutomationUpdatePayload;
            scope: string;
            correlationId: string;
          },
        ) => adapter.markAsPending(state),
        removeOnePessimistic: (state, payload: { data: number; jobTemplateId: number }) =>
          defaultActions.removeOnePessimistic(state, payload),
        fetchOptions: ngrxEvent<{ id: number }>(),
        fetchOptionsSuccess: ngrxEvent<WorkflowAutomationOptionsData>(),
        fetchOptionsFailed: ngrxEvent<{ id: number; error: HttpErrorResponse }>(),
      };
    },
  });

const JobTemplateWorkflowAutomationsActions: PickActions<
  typeof actions,
  | 'fetchJobTemplateAutomations'
  | 'createOnePessimistic'
  | 'updateOnePessimistic'
  | 'removeOnePessimistic'
  | 'fetchOptions'
> = actions;

export {
  JobTemplateWorkflowAutomationsActions,
  namespace as JobTemplateWorkflowAutomationsNamespace,
  reducer as JobTemplateWorkflowAutomationsReducer,
  selectors as JobTemplateWorkflowAutomationsSelectors,
};
